import React from 'react'

import { ModalInjectedProps, ConfirmationDialog } from '@sketch/components'
import {
  useUnpublishShareMutation,
  GetProfilePublicationsDocument,
  GetProfilePublicationsQueryVariables,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { ShareForShareDropdown } from '../../../shares/components/ShareActionsDropdown'

interface UnpublishPublicationModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  workspaceIdentifier?: string
  publicCommentsEnabled: boolean
  publicInspectEnabled: boolean
  publicAccessLevel: ShareForShareDropdown['publicAccessLevel']
  onUnpublish?: () => void
}

export const UnpublishPublicationModal = ({
  hideModal,
  shareName,
  shareIdentifier,
  workspaceIdentifier,
  publicCommentsEnabled = false,
  publicInspectEnabled = false,
  publicAccessLevel,
  onUnpublish,
}: UnpublishPublicationModalProps) => {
  const { showToast } = useToast()

  const [unpublishShare, { loading: isUnpublishing }] =
    useUnpublishShareMutation({
      onError: 'show-toast',
      onCompleted: () => {
        showToast(`"${shareName}" successfully unpublished from profile`)
        hideModal()
        onUnpublish?.()
      },
      refetchQueries: [
        {
          query: GetProfilePublicationsDocument,
          variables: {
            workspaceIdentifier,
          } as GetProfilePublicationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
    })

  const handleConfirm = () => {
    unpublishShare({
      variables: {
        shareIdentifier,
        publicCommentsEnabled,
        publicInspectEnabled,
        publicAccessLevel,
      },
    })
  }

  return (
    <ConfirmationDialog
      hideModal={hideModal}
      title={'Remove Document from Profile?'}
      onConfirm={handleConfirm}
      confirmButton={{
        text: 'Remove',
        variant: 'primary',
        loading: isUnpublishing,
      }}
      cancelButton={{
        text: 'Cancel',
        disabled: isUnpublishing,
      }}
    >
      <p>
        &ldquo;<strong>{shareName}</strong>&rdquo; will no longer appear on your
        public profile, but will still be accessible to anyone with a public
        link.
      </p>
      <p>
        To make this document completely private, please review and edit its
        share settings.
      </p>
    </ConfirmationDialog>
  )
}
